import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { fieldsValidators } from '@shared/constants/field-name-validators';
import { Country, ExternalUser, GridDrawerComponent } from '../../models';
import { CountryService } from '../../services';
@Component({
  standalone: false,
  selector: 'gstar-upsert-external-user',
  templateUrl: 'upsert-external-user.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsertExternalUserComponent
  extends GridDrawerComponent<ExternalUser>
  implements OnInit
{
  private readonly fb = inject(FormBuilder);
  private readonly countryService = inject(CountryService);

  public readonly maxNameLength = 255;
  public readonly maxOrganizationLength = 255;
  enableUpdate: boolean = false;

  public form: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    firstName: [
      '',
      [
        Validators.required,
        Validators.maxLength(this.maxNameLength),
        Validators.pattern(fieldsValidators.lettersWithSpaces),
      ],
    ],
    lastName: [
      '',
      [
        Validators.required,
        Validators.maxLength(this.maxNameLength),
        Validators.pattern(fieldsValidators.lettersWithSpaces),
      ],
    ],
    organization: [
      '',
      [Validators.required, Validators.maxLength(this.maxOrganizationLength)],
    ],
    territory: [null, [Validators.required]],
    isActive: true,
  });

  private prevModel!: ExternalUser;

  selectedCountry!: Country;

  destroyRef = inject(DestroyRef);

  get firstNameField(): AbstractControl<string> {
    return this.form.get('firstName')!;
  }

  get lastNameField(): AbstractControl<string> {
    return this.form.get('lastName')!;
  }

  get organizationField(): AbstractControl<string> {
    return this.form.get('organization')!;
  }

  get territoryField(): AbstractControl<string> {
    return this.form.get('territory')!;
  }

  get emailField(): AbstractControl<string> {
    return this.form.get('email')!;
  }

  ngOnInit(): void {
    this.loadFormValues();
    this.loadSelectedCountry();

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values: ExternalUser) => {
        if (this.isEdit) this.checkChanges(values);
        this.modelChange.emit({
          model: {
            ...this.model,
            ...values,
          },
          prevModel: this.prevModel,
          isValid:
            this.isEdit && this.form.valid
              ? this.enableUpdate
              : this.form.valid,
        });
      });
  }

  loadFormValues(): void {
    if (!this.model) return;

    const currentValue = this.model as ExternalUser;

    this.form.setValue({
      email: currentValue?.email,
      firstName: currentValue?.firstName,
      lastName: currentValue?.lastName,
      isActive: !!currentValue?.isActive,
      organization: currentValue?.organization,
      territory: currentValue.territory ?? null,
    });
  }

  loadSelectedCountry(): void {
    const currentValue = this.model as ExternalUser;
    this.countryService
      .getCountryByCode(currentValue?.territory)
      .subscribe(country => {
        if (country) this.selectedCountry = country;
      });
  }

  countrySelect(country: Country): void {
    this.selectedCountry = country;
    this.form.patchValue({ territory: country.code });
  }

  countryClear(): void {
    this.selectedCountry = undefined!;
    this.form.patchValue({ territory: null });
    this.form.get('territory')?.markAsTouched();
  }

  private checkChanges(values: ExternalUser): void {
    if (values && this.model) {
      this.enableUpdate =
        values.firstName !== this.model.firstName ||
        values.lastName !== this.model.lastName ||
        values.email !== this.model.email ||
        values.organization !== this.model.organization ||
        values.territory !== this.model.territory ||
        values.isActive !== this.model.isActive;
    }
  }
}
